import type { StyleProp, ViewStyle } from "react-native";
import { StyleSheet, Text, View } from "react-native";

import {
  EXCLUSIVE_RIBBON_BACKGROUND_COLOR,
  EXCLUSIVE_RIBBON_LABEL,
  EXCLUSIVE_RIBBON_TEXT_COLOR,
  getExclusiveRibbonCornerSize,
  getExclusiveRibbonMetrics,
  getExclusiveRibbonNativeTextOffset,
  getExclusiveRibbonTextCenter,
  type ExclusiveRibbonVariant,
} from "./recipe";

export interface ExclusiveRibbonNativeProps {
  containerSize?: number;
  style?: StyleProp<ViewStyle>;
  variant: ExclusiveRibbonVariant;
}

/** Native renderer for the shared VIP corner. */
export function ExclusiveRibbon({
  containerSize,
  style,
  variant,
}: ExclusiveRibbonNativeProps) {
  const metrics = getExclusiveRibbonMetrics(variant);
  const cornerSize = getExclusiveRibbonCornerSize({
    containerSize,
    variant,
  });
  const backgroundSize = cornerSize * Math.SQRT2;
  const textCenter = getExclusiveRibbonTextCenter({
    containerSize,
    variant,
  });
  const textOffset = getExclusiveRibbonNativeTextOffset(variant);

  return (
    <View
      pointerEvents="none"
      style={[
        styles.cornerClip,
        { width: cornerSize, height: cornerSize },
        style,
      ]}
    >
      <View
        style={[
          styles.cornerBackground,
          {
            left: -backgroundSize / 2,
            top: -backgroundSize / 2,
            width: backgroundSize,
            height: backgroundSize,
          },
        ]}
      />
      <Text
        numberOfLines={1}
        style={[
          styles.cornerText,
          {
            left: textCenter - metrics.width / 2,
            top: textCenter - metrics.lineHeight / 2,
            width: metrics.width,
            height: metrics.lineHeight,
            fontSize: metrics.fontSize,
            letterSpacing: metrics.letterSpacing,
            lineHeight: metrics.lineHeight,
            transform: [
              { translateX: textOffset.x },
              { translateY: textOffset.y },
              { rotate: "-45deg" },
            ],
          },
        ]}
      >
        {EXCLUSIVE_RIBBON_LABEL}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  cornerClip: {
    position: "absolute",
    left: 0,
    top: 0,
    overflow: "hidden",
  },
  cornerBackground: {
    position: "absolute",
    backgroundColor: EXCLUSIVE_RIBBON_BACKGROUND_COLOR,
    shadowColor: "#1E1A14",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.28,
    shadowRadius: 4,
    elevation: 4,
    transform: [{ rotate: "45deg" }],
  },
  cornerText: {
    position: "absolute",
    color: EXCLUSIVE_RIBBON_TEXT_COLOR,
    fontWeight: "800",
    textAlign: "center",
    textTransform: "uppercase",
  },
});
